import useSetFlinksCredentials from '@/hooks/useApi/useSetFlinksCredentials';
import useSetGlobalPayCredentials from '@/hooks/useApi/useSetGlobalPayCredentials';
import useSetInveriteCredentials from '@/hooks/useApi/useSetInveriteCredentials';
import useSetPlaidCredentials from '@/hooks/useApi/useSetPlaidCredentials';
import { useClientData } from '@/providers/ClientData.provider';
import { IntegrationCardData } from '../types';
import useIntegrationConnectionFlags from './useIntegrationConnectionFlags';
import { IntegrationSetupFormValues } from './useIntegrationSetupFormConfig';

/**
 * Routes the drawer's generic form values to the integration's own credentials endpoint, then
 * flips the account flag the marketplace reads so the card shows Connected without a re-login.
 */
export default function useSaveIntegrationCredentials() {
  const {
    response: { LoginUserName },
  } = useClientData();

  const flinks = useSetFlinksCredentials();
  const plaid = useSetPlaidCredentials();
  const inverite = useSetInveriteCredentials();
  const globalPay = useSetGlobalPayCredentials();
  const { markConnected } = useIntegrationConnectionFlags();

  const saving = flinks.saving || plaid.saving || inverite.saving || globalPay.saving;

  async function saveCredentials(integration: IntegrationCardData, values: IntegrationSetupFormValues) {
    if (!integration.setup) throw new Error(`${integration.id} has no credential setup`);

    const PortalUser = LoginUserName;

    switch (integration.id) {
      case 'flinks':
        await flinks.setFlinksCredentials({ PortalUser, FlinksUrl: values.FlinksUrl.trim() });
        break;
      case 'plaid':
        await plaid.setPlaidCredentials({
          PortalUser,
          PlaidClientID: values.PlaidClientID.trim(),
          PlaidSecretKey: values.PlaidSecretKey.trim(),
          PlaidUrl: values.PlaidUrl.trim(),
        });

        break;
      case 'inverite':
        await inverite.setInveriteCredentials({
          PortalUser,
          InveriteAPIKey: values.InveriteAPIKey.trim(),
          InveriteUrl: values.InveriteUrl.trim(),
        });

        break;
      case 'globalPayments':
        await globalPay.setGlobalPayCredentials({
          PortalUser,
          GlobalPayAppID: values.GlobalPayAppID.trim(),
          GlobalPaySharedSecret: values.GlobalPaySharedSecret.trim(),
        });

        break;
      default:
        throw new Error(`${integration.id} has no credentials endpoint`);
    }

    markConnected(integration.setup.connectedFlag);
  }

  return { saving, saveCredentials };
}
